/**
 * AuditDocUpload - Upload PDF/Word audit documents with annotation & sign-off
 * 
 * Upload existing audit papers, annotate findings, sign off digitally.
 */

import React, { useState, useEffect, useRef } from 'react';
import {
  Upload, FileText, FilePlus, Trash2, Eye,
  Pen, MessageSquare, X, Loader2,
  File, FileImage, FileSpreadsheet,
} from 'lucide-react';
import { Card, CardContent } from '@shared/components/ui/card';
import { Button } from '@shared/components/ui/button';
import { Input } from '@shared/components/ui/input';
import { Label } from '@shared/components/ui/label';
import { Textarea } from '@shared/components/ui/textarea';
import { Badge } from '@shared/components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@shared/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@shared/components/ui/select';
import { toast } from '@shared/components/ui/use-toast';
import { supabase } from '@shared/supabaseClient';

const FILE_ICONS = {
  'application/pdf': FileText,
  'application/msword': FileText,
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': FileText,
  'application/vnd.ms-excel': FileSpreadsheet,
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': FileSpreadsheet,
  'image/jpeg': FileImage,
  'image/png': FileImage,
};

function formatBytes(bytes) {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
}

export default function AuditDocUpload({ user }) {
  const [documents, setDocuments] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [annotationDialog, setAnnotationDialog] = useState(null);
  const [annotationText, setAnnotationText] = useState('');
  const [_annotations, setAnnotations] = useState([]);
  const fileInputRef = useRef(null);

  // Upload form state
  const [uploadTitle, setUploadTitle] = useState('');
  const [uploadCategory, setUploadCategory] = useState('inspection');
  const [uploadNotes, setUploadNotes] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);

  useEffect(() => { loadDocuments(); }, []);

  const loadDocuments = async () => {
    try {
      setIsLoading(true);
      const { data, error } = await supabase
        .from('audit_attachments')
        .select('*')
        .is('response_id', null) // standalone uploads, not response-attached
        .order('uploaded_at', { ascending: false })
        .limit(50);

      if (!error) setDocuments(data || []);
    } catch (err) {
      console.error('Error loading docs:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const maxSize = 50 * 1024 * 1024; // 50MB
    if (file.size > maxSize) {
      toast({ title: 'File too large', description: 'Max 50MB', variant: 'destructive' });
      return;
    }
    setSelectedFile(file);
    if (!uploadTitle) setUploadTitle(file.name.replace(/\.[^.]+$/, ''));
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      toast({ title: 'No file selected', variant: 'destructive' });
      return;
    }
    try {
      setUploading(true);
      const ext = selectedFile.name.split('.').pop();
      const path = `audit-documents/${user.depot_id || 'general'}/${Date.now()}.${ext}`;

      const { error: uploadError } = await supabase.storage
        .from('audit-evidence')
        .upload(path, selectedFile);

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage.from('audit-evidence').getPublicUrl(path);

      const { error: insertError } = await supabase.from('audit_attachments').insert({
        file_url: publicUrl,
        file_name: selectedFile.name,
        mime_type: selectedFile.type,
        file_size: selectedFile.size,
        attachment_type: 'uploaded_audit_file',
        caption: uploadTitle,
        notes: uploadNotes || null,
        uploaded_by: user.id,
      });

      if (insertError) throw insertError;

      toast({ title: 'Document Uploaded', description: uploadTitle });
      setShowUploadDialog(false);
      resetUploadForm();
      loadDocuments();
    } catch (error) {
      console.error('Upload error:', error);
      toast({ title: 'Upload Error', description: error.message, variant: 'destructive' });
    } finally {
      setUploading(false);
    }
  };

  const resetUploadForm = () => {
    setSelectedFile(null);
    setUploadTitle('');
    setUploadCategory('inspection');
    setUploadNotes('');
  };

  const handleDelete = async (doc) => {
    if (!confirm(`Delete "${doc.caption || doc.file_name}"?`)) return;
    try {
      await supabase.from('audit_attachments').delete().eq('id', doc.id);
      toast({ title: 'Document Deleted' });
      loadDocuments();
    } catch (error) {
      console.error('Error deleting:', error);
    }
  };

  const handleAddAnnotation = async () => {
    if (!annotationText.trim() || !annotationDialog) return;
    try {
      // Store annotations in the notes field since there's no metadata column
      const existingNotes = annotationDialog.notes || '';
      const timestamp = new Date().toISOString();
      const annotator = user.full_name || user.email;
      const newNote = `[${timestamp}] ${annotator}: ${annotationText}`;
      const updatedNotes = existingNotes ? `${existingNotes}\n${newNote}` : newNote;

      await supabase.from('audit_attachments').update({
        notes: updatedNotes,
      }).eq('id', annotationDialog.id);

      toast({ title: 'Annotation Added' });
      setAnnotationDialog(null);
      setAnnotationText('');
      loadDocuments();
    } catch (error) {
      console.error('Error adding annotation:', error);
    }
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">Document Upload</h2>
          <p className="text-sm text-muted-foreground">Upload PDF/Word audit documents, annotate, and sign off</p>
        </div>
        <Button onClick={() => setShowUploadDialog(true)}>
          <Upload className="h-4 w-4 mr-2" /> Upload Document
        </Button>
      </div>

      {/* Documents Grid */}
      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1,2,3].map(i => <Card key={i} className="h-40 animate-pulse bg-muted/20" />)}
        </div>
      ) : documents.length === 0 ? (
        <Card className="p-12 text-center border-dashed">
          <Upload className="h-10 w-10 mx-auto text-muted-foreground/30 mb-3" />
          <p className="font-medium text-muted-foreground">No documents uploaded</p>
          <p className="text-sm text-muted-foreground mb-4">
            Upload PDF/Word audit documents to annotate and track
          </p>
          <Button variant="outline" onClick={() => setShowUploadDialog(true)}>
            <Upload className="h-4 w-4 mr-2" /> Upload First Document
          </Button>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {documents.map(doc => {
            const Icon = FILE_ICONS[doc.file_type] || File;
            const annotationCount = doc.notes ? doc.notes.split('\n').filter(l => l.startsWith('[')).length : 0;
            return (
              <Card key={doc.id} className="group hover:border-amber-500/50 transition-all">
                <CardContent className="p-4">
                  <div className="flex items-start gap-3 mb-3">
                    <div className="p-2 rounded-lg bg-amber-500/10 text-amber-600 shrink-0">
                      <Icon className="h-5 w-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-medium text-sm truncate">{doc.caption || doc.file_name}</h3>
                      <p className="text-xs text-muted-foreground">{doc.file_name}</p>
                      <p className="text-xs text-muted-foreground mt-0.5">{formatBytes(doc.file_size)}</p>
                    </div>
                  </div>

                  {annotationCount > 0 && (
                    <Badge variant="outline" className="text-[10px] mb-3">
                      <MessageSquare className="h-2.5 w-2.5 mr-1" />
                      {annotationCount} annotation{annotationCount !== 1 ? 's' : ''}
                    </Badge>
                  )}

                  <div className="flex items-center gap-2 mt-auto">
                    {doc.file_url && (
                      <Button variant="outline" size="sm" className="h-7 text-xs" asChild>
                        <a href={doc.file_url} target="_blank" rel="noopener noreferrer">
                          <Eye className="h-3 w-3 mr-1" /> View
                        </a>
                      </Button>
                    )}
                    <Button
                      variant="outline" size="sm" className="h-7 text-xs"
                      onClick={() => { setAnnotationDialog(doc); setAnnotationText(''); }}
                    >
                      <Pen className="h-3 w-3 mr-1" /> Annotate
                    </Button>
                    <Button
                      variant="ghost" size="icon" className="h-7 w-7 text-red-500 ml-auto opacity-0 group-hover:opacity-100"
                      onClick={() => handleDelete(doc)}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Upload Dialog */}
      <Dialog open={showUploadDialog} onOpenChange={o => { if (!o) setShowUploadDialog(false); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Upload Audit Document</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>File *</Label>
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png"
                className="hidden"
                onChange={handleFileSelect}
              />
              {selectedFile ? (
                <div className="flex items-center gap-2 p-3 rounded-lg border bg-muted/30">
                  <FileText className="h-4 w-4 text-amber-500" />
                  <span className="text-sm flex-1 truncate">{selectedFile.name}</span>
                  <span className="text-xs text-muted-foreground">{formatBytes(selectedFile.size)}</span>
                  <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => setSelectedFile(null)}>
                    <X className="h-3 w-3" />
                  </Button>
                </div>
              ) : (
                <Button variant="outline" className="w-full h-20 border-dashed" onClick={() => fileInputRef.current?.click()}>
                  <div className="flex flex-col items-center gap-1">
                    <FilePlus className="h-5 w-5 text-muted-foreground" />
                    <span className="text-xs">Choose PDF, Word, Excel or Image</span>
                  </div>
                </Button>
              )}
            </div>
            <div className="space-y-1.5">
              <Label>Document Title</Label>
              <Input value={uploadTitle} onChange={e => setUploadTitle(e.target.value)} placeholder="Auto-filled from filename" />
            </div>
            <div className="space-y-1.5">
              <Label>Category</Label>
              <Select value={uploadCategory} onValueChange={setUploadCategory}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="inspection">Inspection Report</SelectItem>
                  <SelectItem value="compliance">Compliance Certificate</SelectItem>
                  <SelectItem value="risk_assessment">Risk Assessment</SelectItem>
                  <SelectItem value="procedure">Procedure / Policy</SelectItem>
                  <SelectItem value="training">Training Record</SelectItem>
                  <SelectItem value="incident">Incident Report</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Notes</Label>
              <Textarea value={uploadNotes} onChange={e => setUploadNotes(e.target.value)} placeholder="Optional notes..." rows={2} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUploadDialog(false)}>Cancel</Button>
            <Button onClick={handleUpload} disabled={uploading || !selectedFile}>
              {uploading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Upload className="h-4 w-4 mr-2" />}
              Upload
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Annotation Dialog */}
      <Dialog open={!!annotationDialog} onOpenChange={o => { if (!o) setAnnotationDialog(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Annotate Document</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* Show existing annotations */}
            {annotationDialog?.notes && (
              <div className="space-y-2 max-h-40 overflow-y-auto">
                {annotationDialog.notes.split('\n').filter(l => l.trim()).map((line, i) => (
                  <div key={i} className="p-2 rounded bg-muted/30 text-sm">
                    <p>{line}</p>
                  </div>
                ))}
              </div>
            )}
            <div className="space-y-1.5">
              <Label>New Annotation</Label>
              <Textarea
                value={annotationText}
                onChange={e => setAnnotationText(e.target.value)}
                placeholder="Add finding, observation, or note..."
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAnnotationDialog(null)}>Cancel</Button>
            <Button onClick={handleAddAnnotation} disabled={!annotationText.trim()}>
              <Pen className="h-4 w-4 mr-2" /> Add Annotation
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
